import {
  RemoteParticipant,
} from "twilio-video";
import { useEffect, useRef } from "react";
import { RemoteParticipantData } from "store/remote-participants/types";
import { useTrackEventHandlers } from "hooks/useTrackEventHandlers";
import { updateRemoteParticipant } from "store/remote-participants/slice";
import { useDispatch } from "react-redux";

export function useRemoteParticipant(
  participant: RemoteParticipant,
  participantState: RemoteParticipantData
) {
  const dispatch = useDispatch();
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const {
    handleRemoteTrackEvents,
    handleRemoteTrackSubscribedEvent,
    handleDataTrackSubscribedEvent,
    handleTrackUnsubscribedEvent,
    setInitialTracks,
  } = useTrackEventHandlers(participant, participantState, videoRef, audioRef);

  /** Set initial tracks, and set up twilio event listeners.
   * The participant dependency will only change when a
   * participant joins or leaves the room.
   */
  useEffect(() => {
    participant.tracks.forEach((publication) => {
      if (publication.isSubscribed && publication.track) {
        handleRemoteTrackEvents(publication.track);
      }

      publication.on("subscribed", handleRemoteTrackEvents);
      publication.on("subscribed", handleRemoteTrackSubscribedEvent);
    });

    participant.on("trackSubscribed", handleRemoteTrackSubscribedEvent);
    participant.on("trackUnsubscribed", handleTrackUnsubscribedEvent);

    setInitialTracks();

    return () => {
      participant.removeListener("trackSubscribed", handleRemoteTrackSubscribedEvent);
      participant.removeListener(
        "trackUnsubscribed",
        handleTrackUnsubscribedEvent
      );
      participant.tracks.forEach((publication) => {
        if (publication.track?.kind !== "data") {
          publication.removeAllListeners();
          publication.track?.removeAllListeners();
        }
      });
      dispatch(updateRemoteParticipant({ sid: participant.sid, isSharingScreen: false }))
    };
  }, [
    dispatch,
    participant,
    handleRemoteTrackEvents,
    handleRemoteTrackSubscribedEvent,
    handleTrackUnsubscribedEvent,
    setInitialTracks,
    handleDataTrackSubscribedEvent
  ]);

  useEffect(() => {
    participant.on("trackSubscribed", handleDataTrackSubscribedEvent);

    return () => {
      participant.removeListener("trackSubscribed", handleDataTrackSubscribedEvent);
    };
  },[handleDataTrackSubscribedEvent, participant]);

  return {
    audioRef,
    videoRef,
  };
}
